import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import en from '@/i18n/en.json';
import pl from '@/i18n/pl.json';

/** Słowniki JSON — nowy język = nowy plik (konwencja BillVis). */
const DICTS = { pl, en } as const;

export type Lang = keyof typeof DICTS;
type Dict = Record<string, string>;

const STORAGE_KEY = 'vendi.lang';

type I18nContextValue = { lang: Lang; setLang: (l: Lang) => void; t: (key: string) => string };

const I18nContext = createContext<I18nContextValue | undefined>(undefined);

function initialLang(): Lang {
  const stored = localStorage.getItem(STORAGE_KEY);
  return stored === 'en' || stored === 'pl' ? stored : 'pl';
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(initialLang);

  const setLang = useCallback((l: Lang) => {
    setLangState(l);
    localStorage.setItem(STORAGE_KEY, l);
    document.documentElement.lang = l;
  }, []);

  const t = useCallback(
    (key: string): string => {
      const dict = DICTS[lang] as Dict;
      return dict[key] ?? (DICTS.pl as Dict)[key] ?? key;
    },
    [lang],
  );

  const value = useMemo<I18nContextValue>(() => ({ lang, setLang, t }), [lang, setLang, t]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n musi być wewnątrz I18nProvider');
  return ctx;
}
